import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { useSceneNodes } from "./SecondaryScene";
import * as THREE from "three";
import CameraManager from "../manager/CameraManager";
import useFlowStore from "../store/flowStore";
import useCursorStore from "../store/cursorStore";
import { audios, subtitles } from "../../assets/assets";

export default function BuzzerScene({interactUIRef}) {
  const { buzzer } = useSceneNodes();
  const { triggerFuseeLaunch, setShowInteractUI, playAudioFile } = useFlowStore();
  const { setCursorScale, setCursorInteractive } = useCursorStore.getState();

  const beatLoopRef = useRef(null); // 👈 pour pouvoir l’arrêter après

  // PLAY HEARTBEAT SOUND ON INIT
  useEffect(() => {
    if (!buzzer) return;

    const { currentStep, flowSteps } = useFlowStore.getState();
    const step = flowSteps[currentStep];

    if (step?.label === "Buzzer") {
      CameraManager.reset();
      CameraManager.switchCamera("camera_6", {near: 0.01, far: 10000});

      playAudioFile(
        audios.instruction_buzzer,
        subtitles.instruction_buzzer
      );

      const beatStart = new Audio(audios.heart_beating_appears);
      const beatLoop = new Audio(audios.heart_beating);
      beatLoop.loop = true;
      beatLoopRef.current = beatLoop;

      setShowInteractUI(true);

      // Lancer le premier son
      beatStart.play().catch(console.warn);

      beatStart.onended = () => {
        beatLoop.play().catch(console.warn);
      };

      setTimeout(() => {
        CameraManager.enableMouseLook(1, 10);
      }, 1000);
    }
  }, [buzzer, useFlowStore((s) => s.currentStep)]);

  // SET BUZZER INTERACTIONS
  useEffect(() => {
    if (!buzzer) return;

    const button = buzzer.getObjectByName("buzzer_button");
    setCursorInteractive(true);

    if (button) {
      button.visible = true;
      button.raycast = THREE.Mesh.prototype.raycast;
      button.userData.onPointerOver = () => setCursorScale(3.5);
      button.userData.onPointerOut = () => setCursorScale(1);

      // put a light on button
      const light = new THREE.PointLight(0xffffff, 1, 10);
      light.position.set(0, 0, 0);
      button.add(light);

      button.material.emissiveIntensity = 0.2;
      button.userData.isClickable = true;

      let hasClicked = false;
      button.userData.onClick = async () => {
        if (hasClicked) return;
        hasClicked = true;

        button.userData.isClickable = false;

        if (interactUIRef?.current) {
          await interactUIRef.current.exit();
        }

        const startY = button.position.y;

        gsap.to(button.position, {
          y: startY - 0.02,
          duration: 0.1,
          ease: "power2.in",
          onComplete: () => {
            gsap.to(button.position, {
              y: startY,
              duration: 0.15,
              ease: "bounce.out",
            });
          },
        });


        // 🎬 Effet visuel de rebond en scale
        gsap.to(button.scale, {
          x: 0.9,
          y: 0.9,
          z: 0.9,
          duration: 0.1,
          yoyo: true,
          repeat: 1,
          ease: "power2.inOut",
        });

        CameraManager.resetLookTargetWithAnimation({
          duration: 1,
          onComplete: () => {

            // ROCKET LAUNCH SOUND
            const audio = new Audio(audios.rocket_launch);
            audio.play().catch(console.warn);

            // ⏱️ Stop heartbeat loop 8 seconds after click
            setTimeout(() => {
              if (beatLoopRef.current) {
                beatLoopRef.current.pause();
                beatLoopRef.current.currentTime = 0;
              }
            }, 8000);

            // 🚀 Launch the camera and the rocket
            setTimeout(() => {
              triggerFuseeLaunch();
            }, 1000);
          },
        });
      };
    }
  }, [buzzer]);

  const handleClick = (e) => {
    e.stopPropagation();
    const o = e.object;
    console.log("Clicked on buzzer", o);
    o.userData.isClickable && o.userData.onClick?.();
  };

  if (!buzzer) return null;

  return <primitive object={buzzer} onPointerDown={handleClick} />;
}
